import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../store/gameStore';
import { gameAPI, setCaseGenerationInProgress } from '../services/gameAPI';
import { toast } from './use-toast';
import { themeConfig } from '../constants/themes';
import {
  hasAuthoritativeSessionPayload,
  shouldReloadStoredSession,
} from '../utils/gameHydrationGuards';
import { hasFeaturedSessionPrefix, isFeaturedSessionId } from '../utils/featuredSession';
import {
  clearGenerationRecovery,
  clearGenerationSession,
  getGenerationJobId,
  isGenerationInProgress,
  resolveGenerationLifecycle,
  setGenerationInProgress,
  setGenerationJobId,
  writeGenerationRecovery,
} from '../utils/generationLifecycle';

// Polling cadence for async case generation jobs.
// Capped linear-ramp backoff: a burst of fast polls, then a gentle ramp up to
// a max interval. The loading bar is decoupled from poll cadence, so widening
// the interval mid-generation has no UX impact. A server-reported progress
// increase resets the cadence to fast so each stage transition (and the
// near-done tail) stays responsive.
const GENERATION_POLL_INTERVAL_MIN_MS = 2500;
const GENERATION_POLL_INTERVAL_MAX_MS = 8000;
const GENERATION_POLL_BACKOFF_STEP_MS = 1500;
const GENERATION_POLL_FAST_POLLS = 5;
const MAX_TRANSIENT_POLL_ERRORS = 3;

// Navigation delay constants (milliseconds)
const NAVIGATION_DELAY_SHORT = 3000;  // For auth errors and retry navigation
const NAVIGATION_DELAY_LONG = 5000;   // For network errors - give user time to read toast

/**
 * useGameSession - owns the whole /game session lifecycle: generation start and
 * polling, recovery state, featured-session compat load, and refresh restore.
 *
 * Extracted verbatim from GamePage so a second presentation of the same
 * session (the /game3d view) can run on identical logic instead of a
 * second copy of it. Render-stage surfaces live in `GameSessionGate`.
 */
export const useGameSession = () => {
  const navigate = useNavigate();
  const {
    gameStatus,
    selectedTheme,
    selectedDifficulty,
    hydrateGeneratedSession,
    sessionId: storedSessionId,
    caseId,
    caseData,
    narrativeText,
    guideCharacter,
    loadSavedGame,
    locations,
    updateGameState,
  } = useGameStore();
  // A featured case is authored, not generated: it has no job to start or poll,
  // no generation lifecycle in sessionStorage, and its theme/difficulty arrive
  // with the compat `load` payload rather than from the difficulty flow. It
  // therefore never shows the case-generation surface — the lightweight
  // restore spinner covers its only wait, the compat `load` round trip.
  const isFeaturedSession = isFeaturedSessionId(storedSessionId);
  // Prefixed but slugless: a corrupted persisted id. It is NOT a generated
  // session, so it must never fall through into the generation surface.
  const isMalformedFeaturedSession = hasFeaturedSessionPrefix(storedSessionId) && !isFeaturedSession;
  const [loading, setLoading] = useState(!isFeaturedSession && !isMalformedFeaturedSession);
  const [generationProgress, setGenerationProgress] = useState(null);
  const [generationRecovery, setGenerationRecovery] = useState(null);
  const [generationRetryNonce, setGenerationRetryNonce] = useState(0);
  // Lightweight restore-from-backend state (page refresh / archive resume).
  // Kept separate from `loading` so we never show the multi-minute
  // case-generation UI when we are only re-fetching an existing session.
  // Only show the restore spinner when there is actually something to wait
  // for: FeaturedCaseEntry hydrates the store before it hands off to /game, so
  // an already-authoritative payload must render the game immediately.
  const [restoring, setRestoring] = useState(() => (
    isFeaturedSession && !hasAuthoritativeSessionPayload({
      sessionId: storedSessionId,
      caseId,
      gameStatus,
      caseData,
      guideCharacter,
    })
  ));
  const initializationAttemptedRef = React.useRef(false); // Track if we've already tried to initialize
  const initializationInProgressRef = React.useRef(false);
  const initializationOwnerRef = React.useRef(null);
  const activeEffectAttemptRef = React.useRef(null);
  const pendingNavigationRef = React.useRef(null);
  const generationStartControllerRef = React.useRef(null);
  const generationPollControllerRef = React.useRef(null);

  const abortGenerationRequests = useCallback(() => {
    gameAPI.cancelStartGame();
    setCaseGenerationInProgress(false);
    if (generationStartControllerRef.current) {
      generationStartControllerRef.current.abort();
      generationStartControllerRef.current = null;
    }
    if (generationPollControllerRef.current) {
      generationPollControllerRef.current.abort();
      generationPollControllerRef.current = null;
    }
  }, []);

  const clearPendingNavigation = useCallback(() => {
    if (pendingNavigationRef.current) {
      clearTimeout(pendingNavigationRef.current);
      pendingNavigationRef.current = null;
    }
  }, []);

  const scheduleDifficultyNavigation = useCallback((delayMs = NAVIGATION_DELAY_SHORT) => {
    clearPendingNavigation();
    pendingNavigationRef.current = setTimeout(() => {
      pendingNavigationRef.current = null;
      navigate('/difficulty');
    }, delayMs);
  }, [clearPendingNavigation, navigate]);

  const scheduleNavigation = useCallback((path, delayMs) => {
    clearPendingNavigation();
    pendingNavigationRef.current = setTimeout(() => {
      pendingNavigationRef.current = null;
      navigate(path);
    }, delayMs);
  }, [clearPendingNavigation, navigate]);

  const resetGenerationRecovery = useCallback(() => {
    clearPendingNavigation();
    abortGenerationRequests();
    clearGenerationSession();
    clearGenerationRecovery();
    setGenerationRecovery(null);
    setGenerationProgress(null);
    initializationAttemptedRef.current = false;
    initializationInProgressRef.current = false;
    initializationOwnerRef.current = null;
  }, [abortGenerationRequests, clearPendingNavigation]);

  const handleRetryGeneration = useCallback(() => {
    resetGenerationRecovery();
    setLoading(true);
    setGenerationRetryNonce(prev => prev + 1);
  }, [resetGenerationRecovery]);

  const handleChangeDifficulty = useCallback(() => {
    resetGenerationRecovery();
    setLoading(true);
    navigate('/difficulty');
  }, [navigate, resetGenerationRecovery]);

  const handleViewArchive = useCallback(() => {
    resetGenerationRecovery();
    setLoading(true);
    navigate('/archive');
  }, [navigate, resetGenerationRecovery]);

  // Cleanup on unmount - abort in-flight requests without dropping resumable job state
  useEffect(() => {
    return () => {
      // If component unmounts while generating, clean up the flag
      // This prevents stale state if user navigates away
      gameAPI.cancelStartGame();
      setCaseGenerationInProgress(false);
      if (generationStartControllerRef.current) {
        generationStartControllerRef.current.abort();
        generationStartControllerRef.current = null;
      }
      if (generationPollControllerRef.current) {
        generationPollControllerRef.current.abort();
        generationPollControllerRef.current = null;
      }
      // Clear any pending navigation timeout to prevent stray navigation
      if (pendingNavigationRef.current) {
        clearTimeout(pendingNavigationRef.current);
        pendingNavigationRef.current = null;
      }
      // Note: Don't clear sessionStorage here - we want it to persist across remounts
      // It will be cleared on success or failure in the initializeGame function
    };
  }, []);

  // A persisted `featured:` id with no slug can never be loaded (gameAPI
  // refuses to build a URL for it) and is not a generated session either, so
  // without this it bounces on every visit to /game and the id is never
  // cleared — a soft lock. Treat any featured-PREFIXED id as featured for
  // routing and cleanup: drop it once and go home.
  useEffect(() => {
    if (!isMalformedFeaturedSession) return;
    console.warn('[useGameSession] Dropping malformed featured session id:', storedSessionId);
    updateGameState({
      sessionId: null,
      caseId: null,
      gameStatus: 'not_started',
    });
    setLoading(false);
    setRestoring(false);
    navigate('/');
  }, [isMalformedFeaturedSession, storedSessionId, updateGameState, navigate]);

  useEffect(() => {
    if (isMalformedFeaturedSession) return;
    // Featured sessions bypass the whole generation lifecycle: no stale
    // sessionStorage job may hijack the page, and no recovery surface applies.
    // They still drop a leftover TERMINAL recovery banner-state, which would
    // otherwise sit in sessionStorage until the next generated case picks it
    // up. An in-flight generation (job id present, or a start request still
    // running) is deliberately left untouched — clearing it would orphan the
    // pro-gen recovery flow for a case that is still being built elsewhere.
    if (isFeaturedSession && !getGenerationJobId() && !isGenerationInProgress()) {
      clearGenerationRecovery();
    }

    if (!isFeaturedSession && !generationRecovery) {
      const lifecycle = resolveGenerationLifecycle();

      if (lifecycle.status === 'unknown_maybe_running') {
        setGenerationRecovery({
          jobId: lifecycle.jobId,
          referenceId: lifecycle.jobId,
          message: 'This case generation may still be running, but the local recovery window expired. Check your archive or choose a difficulty to start fresh.',
          retryable: false,
          kind: 'unknown_maybe_running',
        });
        setLoading(true);
        setGenerationProgress(null);
        initializationAttemptedRef.current = false;
        return;
      }

      if (lifecycle.status === 'idle' && lifecycle.clearedTerminalRecovery && !storedSessionId) {
        updateGameState({
          gameStatus: 'not_started',
          selectedDifficulty: null,
          sessionId: null,
        });
        setLoading(false);
        setGenerationProgress(null);
        initializationAttemptedRef.current = false;
        navigate('/difficulty');
        return;
      }
    }

    const storedJobId = isFeaturedSession ? null : getGenerationJobId();
    const startRequestInProgress = isFeaturedSession ? false : isGenerationInProgress();

    if (!isFeaturedSession && generationRecovery) {
      setLoading(true);
      setGenerationProgress(null);
      initializationAttemptedRef.current = false;
      return;
    }

    // Don't redirect if generation is in progress - Zustand may still be rehydrating
    // This prevents false redirects when the component remounts during case generation
    // A featured session is exempt: the compat `load` below supplies its theme
    // and difficulty, so an empty store is expected here rather than a fault.
    if (!isFeaturedSession && (gameStatus === 'not_started' || !selectedTheme || !selectedDifficulty)) {
      if (startRequestInProgress || storedJobId) {
        console.log('[useGameSession] Waiting for Zustand rehydration - generation in progress');
        return; // Don't redirect, wait for rehydration
      }
      navigate(selectedTheme && !selectedDifficulty ? '/difficulty' : '/');
      return;
    }

    const attemptId = Symbol('game-page-init-attempt');
    let ignoreAttempt = false;
    activeEffectAttemptRef.current = attemptId;

    const isCurrentAttempt = () => (
      !ignoreAttempt && activeEffectAttemptRef.current === attemptId
    );

    const beginInitialization = () => {
      initializationInProgressRef.current = true;
      initializationOwnerRef.current = attemptId;
    };

    const releaseInitialization = () => {
      if (initializationOwnerRef.current === attemptId) {
        initializationInProgressRef.current = false;
        initializationOwnerRef.current = null;
      }
    };

    // Initialize game with backend API
    const initializeGame = async () => {
      // Prevent duplicate initialization - check both ref and sessionStorage
      // sessionStorage persists across component remounts (e.g., during Clerk token refresh)
      const storedJobId = getGenerationJobId();
      const shouldResumeStoredJob = Boolean(storedJobId && isGenerationInProgress());
      if (storedJobId && !shouldResumeStoredJob) {
        console.log('[useGameSession] Clearing stale generation job before starting a fresh request');
        clearGenerationSession();
      }

      const existingJobId = shouldResumeStoredJob ? storedJobId : null;
      if (initializationInProgressRef.current || (initializationAttemptedRef.current && !existingJobId)) {
        console.log('[useGameSession] Skipping duplicate initialization - already in progress');
        return;
      }

      const makeAbortError = () => {
        const error = new Error('Generation polling aborted');
        error.name = 'AbortError';
        return error;
      };

      const waitForNextPoll = (signal, delayMs) => new Promise((resolve, reject) => {
        if (signal.aborted) {
          reject(makeAbortError());
          return;
        }

        const handleAbort = () => {
          clearTimeout(timeoutId);
          reject(makeAbortError());
        };
        const timeoutId = setTimeout(() => {
          signal.removeEventListener('abort', handleAbort);
          resolve();
        }, delayMs);

        signal.addEventListener('abort', handleAbort, { once: true });
      });

      const isTransientPollError = (error) => {
        const errorMsg = error.message?.toLowerCase() || '';
        return error.isNetworkError ||
          error.isTimeout ||
          error.code === 'ERR_NETWORK' ||
          error.code === 'ECONNRESET' ||
          error.code === 'ECONNABORTED' ||
          errorMsg.includes('network error') ||
          errorMsg.includes('failed to fetch') ||
          errorMsg.includes('timeout');
      };

      const makeGenerationFailureError = (job) => {
        const jobError = job?.error || {};
        const message = jobError.user_message ||
          jobError.message ||
          jobError.error ||
          'Case generation failed. Please try again.';
        const error = new Error(message);
        error.userMessage = message;
        error.retryable = jobError.retryable !== false;
        error.publicCategory = jobError.public_category || null;
        error.referenceId = jobError.reference_id || job?.job_id || job?.id || null;
        error.generationJob = job;
        return error;
      };

      const isGenerationJobNotFoundError = (error) => {
        return error.response?.status === 404 ||
          error.status === 404 ||
          error.statusCode === 404;
      };

      const extractJobResult = (job) => {
        if (job.progress && isCurrentAttempt()) {
          setGenerationProgress(job.progress);
        }

        if (job.status === 'succeeded') {
          const result = job.result;
          const initialState = result?.initialState;
          const hasUsableSessionPayload = Boolean(
            result?.sessionId &&
            initialState &&
            typeof initialState === 'object' &&
            Array.isArray(initialState.suspects) &&
            Array.isArray(initialState.clues) &&
            Array.isArray(initialState.locations) &&
            Array.isArray(initialState.documents) &&
            initialState.discovered &&
            typeof initialState.discovered === 'object'
          );

          if (!hasUsableSessionPayload) {
            const error = new Error('Case generation completed without usable session data.');
            error.generationJob = job;
            error.missingGenerationResult = true;
            throw error;
          }
          return result;
        }

        if (job.status === 'failed') {
          throw makeGenerationFailureError(job);
        }

        return null;
      };

      const pollGenerationJob = async (jobId) => {
        const controller = new AbortController();
        generationPollControllerRef.current = controller;
        let transientPollErrors = 0;
        let pollCount = 0;
        let lastSeenProgress = null;

        try {
          while (!controller.signal.aborted && isCurrentAttempt()) {
            try {
              const job = await gameAPI.getGenerationJob(jobId, { signal: controller.signal });
              if (!isCurrentAttempt()) {
                throw makeAbortError();
              }
              transientPollErrors = 0;

              // Reset cadence to fast whenever the server reports forward
              // progress, so each generation-stage transition gets a burst of
              // quicker polls. Reads from the same field extractJobResult uses.
              const currentProgress = job?.progress?.progress;
              if (typeof currentProgress === 'number') {
                if (lastSeenProgress === null || currentProgress > lastSeenProgress) {
                  pollCount = 0;
                }
                lastSeenProgress = currentProgress;
              }

              const result = extractJobResult(job);
              if (result) {
                return result;
              }
            } catch (error) {
              if (controller.signal.aborted || error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                throw error;
              }

              if (isTransientPollError(error) && transientPollErrors < MAX_TRANSIENT_POLL_ERRORS) {
                transientPollErrors += 1;
                console.warn(`[useGameSession] Generation poll failed (${transientPollErrors}/${MAX_TRANSIENT_POLL_ERRORS}), retrying`, error);
              } else {
                throw error;
              }
            }

            const delay = Math.min(
              GENERATION_POLL_INTERVAL_MAX_MS,
              GENERATION_POLL_INTERVAL_MIN_MS +
                Math.max(0, pollCount - GENERATION_POLL_FAST_POLLS) * GENERATION_POLL_BACKOFF_STEP_MS,
            );
            pollCount += 1;
            await waitForNextPoll(controller.signal, delay);
          }

          throw makeAbortError();
        } finally {
          if (generationPollControllerRef.current === controller) {
            generationPollControllerRef.current = null;
          }
        }
      };

      let jobId = existingJobId;
      let retriedStaleResume = false;

      try {
        initializationAttemptedRef.current = true;
        beginInitialization();
        clearGenerationRecovery();
        setGenerationRecovery(null);
        setGenerationInProgress(true);
        setLoading(true);

        const startNewGenerationJob = async () => {
          setGenerationInProgress(true);
          const startController = new AbortController();
          generationStartControllerRef.current = startController;
          let job;
          try {
            job = await gameAPI.startGameAsync(
              selectedTheme,
              selectedDifficulty,
              { signal: startController.signal }
            );
          } finally {
            if (generationStartControllerRef.current === startController) {
              generationStartControllerRef.current = null;
            }
          }

          if (!isCurrentAttempt()) {
            return;
          }

          jobId = job.job_id;
          if (!jobId) {
            throw new Error('Case generation did not return a job id.');
          }

          setGenerationJobId(jobId);
          return extractJobResult(job);
        };

        const startOrResumeGeneration = async () => {
          let response = null;

          if (!jobId) {
            response = await startNewGenerationJob();
          }

          if (!isCurrentAttempt()) {
            return null;
          }

          if (!response) {
            response = await pollGenerationJob(jobId);
          }

          return response;
        };

        let response = null;

        while (!response && isCurrentAttempt()) {
          const resumedJobId = jobId === existingJobId ? existingJobId : null;

          try {
            response = await startOrResumeGeneration();
          } catch (error) {
            const isStaleResumeFailure = Boolean(resumedJobId) &&
              !retriedStaleResume &&
              isGenerationJobNotFoundError(error);

            if (isStaleResumeFailure) {
              console.warn('[useGameSession] Stored generation job is unavailable; starting one fresh retry', error);
              retriedStaleResume = true;
              clearGenerationSession();
              setGenerationProgress(null);
              jobId = null;
              continue;
            }

            throw error;
          }
        }

        if (!isCurrentAttempt()) {
          return;
        }

        clearPendingNavigation();

        hydrateGeneratedSession({
          ...response,
          narrativeText: response.openingNarrative,
          theme: response.theme || selectedTheme,
          difficulty: response.difficulty || selectedDifficulty,
        });

        // Clear generation flag - success!
        clearGenerationSession();
        clearGenerationRecovery();
        setGenerationProgress(null);
        setGenerationRecovery(null);
        clearPendingNavigation();
        setLoading(false);

        const failedImageCount = Number(response?.failedImageCount) || 0;
        if (failedImageCount > 0) {
          toast({
            variant: 'destructive',
            title: 'Case loaded with missing images',
            description: `${failedImageCount} image${failedImageCount === 1 ? '' : 's'} couldn't be generated. Your investigation can still proceed — affected items show a placeholder.`
          });
        } else {
          toast({
            title: 'Case loaded',
            description: 'Your investigation begins...'
          });
        }

      } catch (error) {
        if (!isCurrentAttempt()) {
          return;
        }

        console.error('Error initializing game:', error);

        const currentJobId = jobId || getGenerationJobId();
        const hasGenerationJob = Boolean(currentJobId);

        // Check if request was aborted (user navigated away or duplicate request cancelled)
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
          console.log('[useGameSession] Start game request was cancelled');
          if (!hasGenerationJob) {
            clearGenerationSession();
            setGenerationProgress(null);
          }
          if (!hasGenerationJob) {
            initializationAttemptedRef.current = false;
          }
          return; // Don't show error for intentional cancellation
        }

        // Check if this is a network error (connection reset, timeout, etc.)
        // Note: fetch API throws "TypeError: network error" or "TypeError: Failed to fetch"
        const errorMsg = error.message?.toLowerCase() || '';
        const isNetworkError = error.isNetworkError ||
                               error.isTimeout ||
                               error.code === 'ERR_NETWORK' ||
                               error.code === 'ECONNRESET' ||
                               error.code === 'ECONNABORTED' ||
                               errorMsg.includes('network error') ||
                               errorMsg.includes('failed to fetch') ||
                               errorMsg.includes('timeout');

        // Check if this is an auth error (401)
        // Note: auth-session-expired is suppressed during generation, so we handle it here
        const isAuthError = error.isAuthError || error.response?.status === 401;
        const isPreflightFailure = error.isPreflightFailure;
        const isTerminalGenerationFailure = error.generationJob?.status === 'failed';
        const isTerminalMissingResult = error.generationJob?.status === 'succeeded' && error.missingGenerationResult;
        const isGenerationJobNotFound = isGenerationJobNotFoundError(error);
        const isMissingStoredGenerationJob = Boolean(existingJobId) &&
          currentJobId === existingJobId &&
          isGenerationJobNotFound;

        if (isTerminalGenerationFailure) {
          const failedJob = error.generationJob;
          const failedJobError = failedJob?.error || {};
          const recoveryMessage = failedJobError.user_message ||
            failedJobError.message ||
            failedJobError.error ||
            failedJob?.progress?.message ||
            error.userMessage ||
            'Case generation failed. Please try again.';
          const failedJobId = failedJob?.job_id || failedJob?.id || currentJobId;
          const referenceId = failedJobError.reference_id || error.referenceId || failedJobId;
          const recovery = {
            message: recoveryMessage,
            jobId: failedJobId,
            referenceId,
            retryable: failedJobError.retryable !== false,
            kind: failedJobError.kind || 'generation_failed',
            publicCategory: failedJobError.public_category || error.publicCategory || null,
          };

          clearPendingNavigation();
          clearGenerationSession();
          const normalizedRecovery = writeGenerationRecovery(recovery);
          setGenerationProgress(null);
          setGenerationRecovery(normalizedRecovery);
          initializationAttemptedRef.current = false;
          setLoading(true);
          return;
        }

        if (isTerminalGenerationFailure || isTerminalMissingResult || isGenerationJobNotFound || !hasGenerationJob) {
          // Terminal failures, malformed terminal jobs, stale stored jobs, and pre-job failures are safe to clear.
          clearGenerationSession();
          setGenerationProgress(null);
        }

        // Extract user-friendly message from error response
        let errorMessage = 'Failed to start game. Please try again.';

        if (isPreflightFailure) {
          // Pre-flight check failed - case generation never started, safe to retry
          errorMessage = 'Your session expired. Please sign in again to start a new case.';
        } else if (isMissingStoredGenerationJob) {
          errorMessage = 'That case generation job is no longer available. Please start a new case.';
        } else if (isTerminalMissingResult) {
          errorMessage = 'Case generation completed without usable session data. Please try again.';
        } else if (isAuthError) {
          // Auth error during generation - case may still be generating on backend
          errorMessage = 'Session issue occurred during case generation. The case may still be generating - wait 30 seconds and check the Archive, then try signing in again.';
        } else if (isNetworkError) {
          errorMessage = 'Connection lost while generating case. The backend may still be processing. Redirecting you to the Archive to check...';
        } else if (error.userMessage) {
          errorMessage = error.userMessage;
        } else if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          if (typeof detail === 'object' && detail.user_message) {
            errorMessage = detail.user_message;
          } else if (typeof detail === 'string') {
            errorMessage = detail;
          }
        }

        toast({
          title: isPreflightFailure ? 'Session Expired' : (isAuthError ? 'Session Issue' : 'Case Generation Failed'),
          description: errorMessage,
          variant: 'destructive',
          duration: 10000,
        });

        if (isTerminalGenerationFailure || isTerminalMissingResult || isGenerationJobNotFound || !hasGenerationJob) {
          initializationAttemptedRef.current = false; // Allow retry when no backend job can be resumed
        }

        // Keep the loading/recovery surface mounted until delayed navigation completes.

        // Navigate based on error type
        // Store timeout ID so it can be cleared on unmount
        // Defensively clear any existing timeout to prevent multiple concurrent navigations
        if (isPreflightFailure) {
          // Session expired before generation - go to sign in
          scheduleNavigation('/sign-in', NAVIGATION_DELAY_SHORT);
        } else if (isNetworkError) {
          // Network error during generation - backend may still be processing
          // Navigate to Archive where they can see if the case completed
          scheduleNavigation('/archive', NAVIGATION_DELAY_LONG);
        } else if (error.response?.status === 403) {
          // Case generation is locked (tutorial not complete, or admin lock).
          // /difficulty would only fail again on Start; home shows the lock.
          scheduleNavigation('/', NAVIGATION_DELAY_SHORT);
        } else {
          // Other errors (e.g., backend validation failures) - go to retry
          scheduleDifficultyNavigation();
        }
      } finally {
        releaseInitialization();
      }
    };

    // Reload game data from backend if session exists but data is missing (e.g., after page refresh)
    const reloadGameData = async () => {
      if (initializationInProgressRef.current) return;

      try {
        beginInitialization();
        // Suppress the case-generation takeover; show the lightweight
        // restore surface instead while we re-fetch the existing session.
        setLoading(false);
        setRestoring(true);

        const savedData = await gameAPI.loadGameSession(storedSessionId);

        if (!isCurrentAttempt()) {
          return;
        }

        // Load into store using the same method as CaseArchive
        loadSavedGame({
          theme: savedData.theme || 'noir',
          difficulty: savedData.difficulty || 'medium',
          caseId: savedData.caseId,
          sessionId: savedData.sessionId,
          gameState: savedData.gameState,
          messageHistory: savedData.messageHistory,
          guideCharacter: savedData.guideCharacter,
          caseData: savedData.caseData,
          lastImage: savedData.lastImage,
          lastImageVantage2: savedData.lastImageVantage2
        });

        setLoading(false);
        setRestoring(false);
      } catch (error) {
        if (!isCurrentAttempt()) {
          return;
        }

        console.error('Error reloading game data:', error);
        toast({
          title: 'Failed to reload game',
          description: 'Could not restore your session. Returning to menu.',
          variant: 'destructive',
        });
        setLoading(false);
        setRestoring(false);
        if (isFeaturedSession) {
          // The featured record is gone (deleted, or the slug was retired).
          // Drop the dangling session id so a later visit to /game cannot
          // loop on a 404, and never fall through into generation.
          updateGameState({
            sessionId: null,
            caseId: null,
            gameStatus: 'not_started',
          });
        }
        navigate('/');
      } finally {
        releaseInitialization();
      }
    };

    // Only initialize if we don't have a session yet and haven't attempted.
    // `isFeaturedSession` implies a stored session id, so a featured case can
    // never reach `initializeGame()`; the guard is restated for the reader.
    if (!isFeaturedSession && !storedSessionId && !initializationInProgressRef.current && (!initializationAttemptedRef.current || getGenerationJobId())) {
      initializeGame();
    } else if (
      shouldReloadStoredSession({
        sessionId: storedSessionId,
        caseId,
        gameStatus,
        caseData,
        narrativeText,
        guideCharacter,
        locations,
      }) &&
      !initializationInProgressRef.current
    ) {
      // Session exists but data is missing (page was refreshed) - reload from backend
      reloadGameData();
    } else if (storedSessionId) {
      setLoading(false);
      setRestoring(false);
    }

    return () => {
      ignoreAttempt = true;
      if (activeEffectAttemptRef.current === attemptId) {
        activeEffectAttemptRef.current = null;
      }
      if (initializationOwnerRef.current === attemptId) {
        initializationInProgressRef.current = false;
        initializationOwnerRef.current = null;
        if (!getGenerationJobId()) {
          initializationAttemptedRef.current = false;
          clearGenerationSession();
        }
      }
      gameAPI.cancelStartGame();
      setCaseGenerationInProgress(false);
      if (generationStartControllerRef.current) {
        generationStartControllerRef.current.abort();
        generationStartControllerRef.current = null;
      }
      if (generationPollControllerRef.current) {
        generationPollControllerRef.current.abort();
        generationPollControllerRef.current = null;
      }
      if (pendingNavigationRef.current) {
        clearTimeout(pendingNavigationRef.current);
        pendingNavigationRef.current = null;
      }
    };
  }, [gameStatus, selectedTheme, selectedDifficulty, navigate, storedSessionId, isFeaturedSession, caseId, caseData, narrativeText, guideCharacter, locations, loadSavedGame, hydrateGeneratedSession, updateGameState, clearPendingNavigation, scheduleDifficultyNavigation, scheduleNavigation, generationRetryNonce, generationRecovery, isMalformedFeaturedSession]);

  return {
    sessionId: storedSessionId,
    selectedTheme,
    selectedDifficulty,
    theme: themeConfig[selectedTheme] || themeConfig.noir,
    isFeaturedSession,
    isMalformedFeaturedSession,
    loading,
    restoring,
    generationProgress,
    generationRecovery,
    handleRetryGeneration,
    handleChangeDifficulty,
    handleViewArchive,
  };
};

export default useGameSession;
